// Formatting shared by the customer popup (header, Overview, tabs).
// Rule: show plain words, never raw enums; a source we haven't synced says so,
// never renders as 0 or $0.

export const clean = (v) => (v == null ? "" : String(v).trim());

export function money(v, { cents = false } = {}) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: cents ? 2 : 0,
      maximumFractionDigits: cents ? 2 : n % 1 ? 2 : 0,
    }).format(n);
  } catch {
    return `$${n.toLocaleString()}`;
  }
}

// Date-only strings ("2026-09-23") are calendar dates — never shift them through UTC.
function toDate(v) {
  if (!v) return null;
  const s = String(v);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function shortDate(v, withYear) {
  const d = toDate(v);
  if (!d) return "";
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    ...(withYear || !sameYear ? { year: "numeric" } : {}),
  });
}

export function dateTime(v) {
  const d = toDate(v);
  if (!d) return "";
  return d.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
}

// "sales_status" → "Sales status", "PENDING" → "Pending"
export function plainWords(v) {
  const s = clean(v).replace(/[_-]+/g, " ").toLowerCase();
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : "";
}

const FREQ = {
  "2_month": "Every 2 months",
  bi_monthly: "Every 2 months",
  bimonthly: "Every 2 months",
  monthly: "Monthly",
  "1_month": "Monthly",
  quarterly: "Quarterly",
  "3_month": "Quarterly",
  "4_month": "Every 4 months",
  "6_month": "Twice a year",
  semi_annual: "Twice a year",
  annual: "Yearly",
  yearly: "Yearly",
  "12_month": "Yearly",
  weekly: "Weekly",
  as_needed: "As needed",
};

export function frequencyWords(v) {
  const k = clean(v).toLowerCase();
  if (!k) return "";
  return FREQ[k] || plainWords(k);
}

export function formatPhone(v) {
  const digits = clean(v).replace(/\D/g, "");
  const d = digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
  if (d.length === 10) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  return clean(v);
}

export function telHref(v) {
  const digits = clean(v).replace(/\D/g, "");
  if (digits.length === 10) return `tel:+1${digits}`;
  if (digits.length === 11 && digits.startsWith("1")) return `tel:+${digits}`;
  return digits ? `tel:${digits}` : "";
}

// Coverage from the FieldRoutes summary: {held, declared, state}.
export function coverageText(cov, noun = "") {
  if (!cov) return "";
  const what = noun ? ` ${noun}` : "";
  switch (cov.state) {
    case "none":
      return `No${what || " records"} in FieldRoutes yet`;
    case "not_synced":
      return `Not synced — FieldRoutes has ${cov.declared}${what}`;
    case "partial":
      return `${cov.held} of ${cov.declared}${what} synced`;
    case "unknown":
      return "Not synced";
    default:
      return "";
  }
}
